import { useState, useEffect } from 'react';
import { Sparkles, X, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const DISMISS_KEY = 'kf_ls_banner_dismissed_until';

/**
 * Læringssti-forbedring-banner (2026-09-15)
 * Vises på Dashboard hvis kandidat mangler både career_history OG interest_profile.
 * Motiverer til å legge inn data ved å forklare kvalitetsforbedring i Læringssti + CV.
 * Kan avvises for 7 dager via localStorage.
 */
export function LearningSuggestionsBanner() {
  const [shouldShow, setShouldShow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Sjekk dismiss-cooldown i localStorage
    try {
      const until = localStorage.getItem(DISMISS_KEY);
      if (until && Number(until) > Date.now()) {
        setLoading(false);
        return;
      }
    } catch (_) {}

    // Hent kandidatens karriere + interesser parallelt
    (async () => {
      try {
        const [careerRes, interestsRes] = await Promise.all([
          fetch('/api/candidate/career-history', { credentials: 'include' }).catch(() => null),
          fetch('/api/candidate/interests', { credentials: 'include' }).catch(() => null)
        ]);
        const career = careerRes?.ok ? await careerRes.json() : { items: [] };
        const interests = interestsRes?.ok ? await interestsRes.json() : { interests: [] };
        const hasCareer = Array.isArray(career.items) && career.items.length > 0;
        const hasInterests = Array.isArray(interests.interests) && interests.interests.length > 0;
        setShouldShow(!hasCareer && !hasInterests);
      } catch (_) {
        // Silent: ikke vis ved feil
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function dismiss() {
    try {
      // 7 dagers cooldown
      localStorage.setItem(DISMISS_KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000));
    } catch (_) {}
    setDismissed(true);
  }

  if (loading || dismissed || !shouldShow) return null;

  return (
    <div className="bg-gradient-to-br from-purple-50 to-kf-blue-50 border border-purple-200 rounded-xl p-5 mb-6 relative">
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 text-slate-400 hover:text-slate-600"
        aria-label="Skjul denne meldingen"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex gap-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-purple-600" />
          </div>
        </div>
        <div className="flex-1 pr-6">
          <h3 className="font-semibold text-slate-900">Få skreddersydde kursforslag</h3>
          <p className="text-sm text-slate-700 mt-1 leading-relaxed">
            Legg inn <strong>yrkeserfaring</strong> og velg <strong>interesse-områder</strong> —
            så matcher vi kurskatalogen på kompetanseheving.no mot din bakgrunn.
            Du får bedre forslag i Læringssti og en mer komplett CV.
          </p>
          <div className="mt-3 flex gap-3">
            <Link
              to="/settings"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700"
            >
              Kom i gang <ArrowRight className="h-4 w-4" />
            </Link>
            <button
              onClick={dismiss}
              className="text-sm text-slate-600 hover:text-slate-900 px-2"
            >
              Minn meg senere
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
